import * as THREE from 'three'
import vertexShader from '../shaders/vert-audio.glsl.js'
import fragmentShader from '../shaders/frag-audio.glsl.js'

/**
 * A subclass of the base THREE.JS ShaderMaterial that manages appearance of
 * visualizer objects.
 * @param {Object<String: Any>} obj - A collection of settings passed to the
 * material instance.
 */
export default class AudioMaterial extends THREE.ShaderMaterial {
  /**
   * Material default settings.
   * @type {Object<String: Any>}
   */
  static #defaults = {
    palettes: [
      [0xff0000, 0xff0000],
      [0xff0000, 0xff0000]
    ],
    width: 1,
    depth: 1,
    zoom: 1
  }

  /**
   * Each created instance of AudioMaterial.
   * @type {Array<AudioMaterial>}
   */
  static #instances = []

  constructor(obj) {
    const settings = {
      ...AudioMaterial.#defaults,
      ...obj
    }

    Object.entries(settings).forEach(entry => {
      if (entry[1] == null) {
        settings[entry[0]] = AudioMaterial.#defaults[entry[0]]
      }
    })

    // Get randomized wave data
    const { waveX0, waveX1, waveX2, waveY0, waveY1, waveY2 } = AudioMaterial.#generateRandomizedWaves()

    super({
      uniforms: {
        uTexture: { value: null },
        uData: { value: null },
        palettes: {
          value: settings.palettes.map(palette => {
            return {
              c0: new THREE.Color(palette[0]),
              c1: new THREE.Color(palette[1])
            }
          })
        },
        time: { value: 0 },
        timeDelta: { value: 0 },
        wavesX: { value: [waveX0, waveX1, waveX2] },
        wavesY: { value: [waveY0, waveY1, waveY2] },
        jitterOffset0: { value: AudioMaterial.#random(0.0, 1.0) },
        jitterOffset1: { value: AudioMaterial.#random(0.0, 1.0) },
        fakeDataFade: { value: 0 },
        fakeDataFadeOffset: { value: 0 },
        zoom: { value: settings.zoom },
        timeMult: { value: AudioMaterial.#random(0.1, 0.5) }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      defines: {
        PI: Math.PI,
        PI2: Math.PI * 2,
        SHAPE_WIDTH: settings.width,
        SHAPE_DEPTH: settings.depth
      }
    })

    this.transparent = true
    this.side = THREE.DoubleSide

    AudioMaterial.#instances.push(this)
    AudioMaterial.#instances.forEach((instance, index) => {
      instance.uniforms.fakeDataFadeOffset.value = index / AudioMaterial.#instances.length
    })
  }

  // FAKE DATA -----------------------------------------------------------------

  /**
   * Stores a requestAnimationFrame ID.
   * @type {Number}
   */
  #fakeDataRAF

  /**
   * Stores the "direction" of a fake data transition. Set to TRUE when
   * transitioning to fake data.
   * @type {Bool}
   */
  #transitionFakeDataUp

  /**
   * Used to track timing for fake data transitions.
   * @type {Number}
   */
  #fakeDataPreviousTime

  /**
   * Stores the value of fake data at the start of a transition.
   * @type {Number}
   */
  #baseFakeData = 0

  /**
   * States used to track the fake data status of the material.
   * @type {Enum}
   */
  static FakeDataState = {
    USING_FAKE: 0,
    USING_REAL: 1,
    TRANSITION_TO_FAKE: 2,
    TRANSITION_TO_REAL: 3
  }

  /**
   * The current state of a fake data transition.
   * @type {AudioMaterial.FakeDataState}
   */
  get fakeDataState() {
    const value = this.uniforms.fakeDataFade.value
    if (value <= 0) {
      return AudioMaterial.FakeDataState.USING_REAL
    } else if (value >= 1) {
      return AudioMaterial.FakeDataState.USING_FAKE
    } else if (this.#transitionFakeDataUp) {
      return AudioMaterial.FakeDataState.TRANSITION_TO_FAKE
    } else {
      return AudioMaterial.FakeDataState.TRANSITION_TO_REAL
    }
  }

  /**
   * Enables showing "fake" data.
   *
   * Calling this enables a randomly generated pattern to be displayed when
   * there would otherwise be no data passed to uTexture.
   */
  setFakeData() {
    this.#fakeDataPreviousTime = undefined
    cancelAnimationFrame(this.#fakeDataRAF)
    this.uniforms.fakeDataFade.value = 1
    this.#baseFakeData = 1
  }

  /**
   * Unsets the fake data to reveal actual sound data.
   */
  unsetFakeData() {
    this.#fakeDataPreviousTime = undefined
    cancelAnimationFrame(this.#fakeDataRAF)
    this.uniforms.fakeDataFade.value = 0
    this.#baseFakeData = 0
  }

  /**
   * Animates the fake data value from 0 to 1.
   *
   * It is safe to call this during an existing animation. It will pick up where
   * the last one left off and will run a shorter amount of time.
   */
  animateSetFakeData() {
    if (
      this.fakeDataState === AudioMaterial.FakeDataState.TRANSITION_TO_FAKE ||
      this.fakeDataState === AudioMaterial.FakeDataState.USING_FAKE
    ) return
    this.#baseFakeData = this.uniforms.fakeDataFade.value
    this.#fakeDataPreviousTime = undefined
    cancelAnimationFrame(this.#fakeDataRAF)
    this.#transitionFakeDataUp = true
    this.#updateFakeDataValue(document.timeline.currentTime)
  }

  /**
   * Animates the fake data value from 1 to 0.
   *
   * It is safe to call this during an existing animation. It will pick up where
   * the last one left off and will run a shorter amount of time.
   */
  animateUnsetFakeData() {
    if (
      this.fakeDataState === AudioMaterial.FakeDataState.TRANSITION_TO_REAL ||
      this.fakeDataState === AudioMaterial.FakeDataState.USING_REAL
    ) return
    this.#baseFakeData = this.uniforms.fakeDataFade.value
    this.#fakeDataPreviousTime = undefined
    cancelAnimationFrame(this.#fakeDataRAF)
    this.#transitionFakeDataUp = false
    this.#updateFakeDataValue(document.timeline.currentTime)
  }

  /**
   * Callback loop used with requestAnimationFrame to decrement the fakeDataFade
   * uniform over time.
   * @param {DOMHighResTimeStamp}
   */
  #updateFakeDataValue = (timestamp) => {
    const baseDuration = 1500
    let range

    if (this.#transitionFakeDataUp) {
      range = 1 - this.#baseFakeData
    } else {
      range = this.#baseFakeData
    }

    const duration = baseDuration * range

    if (this.#fakeDataPreviousTime === undefined) {
      this.#fakeDataPreviousTime = timestamp
    }
    const delta = timestamp - this.#fakeDataPreviousTime
    let factor = delta / duration

    if (factor < 1 && factor >= 0) {
      this.#fakeDataRAF = requestAnimationFrame(this.#updateFakeDataValue)
    }

    if (this.#transitionFakeDataUp) {
      factor = factor * range + this.#baseFakeData
    } else {
      factor = factor * range + (1 - this.#baseFakeData)
    }
    factor = Math.max(0, Math.min(1, factor))
    if (!this.#transitionFakeDataUp) {
      factor = 1 - factor
    }

    this.uniforms.fakeDataFade.value = factor
  }

  // HELPERS -------------------------------------------------------------------

  /**
   * Generates a random number between in0 and in1.
   * @param {Number} in0 - The lower end of the range used to generate a value
   * @param {Number} in1 - The upper end of the range used to generate a value
   * @return {Number}
   */
  static #random(in0, in1) {
    const min = Math.min(in0, in1)
    const max = Math.max(in0, in1)
    return Math.random() * (max - min) + min
  }

  /**
   * Creates a series of wave objects, each with a randomized count and speed,
   * and a set amplitude. Creates 6 in total: 3 to use for x values and 3 for y.
   * @return {{String: {String: Number}}}
   */
  static #generateRandomizedWaves() {
    const waveX0 = {
      count: AudioMaterial.#random(1, 4),
      speed: AudioMaterial.#random(0.05, 0.1),
      amplitude: 0.2
    }

    const waveX1 = {
      count: AudioMaterial.#random(1, 2),
      speed: -AudioMaterial.#random(0.1, 0.2),
      amplitude: 0.25
    }

    const waveX2 = {
      count: AudioMaterial.#random(1, 4),
      speed: -AudioMaterial.#random(0.05, 0.1),
      amplitude: 0.25
    }

    const waveY0 = {
      count: AudioMaterial.#random(1, 4),
      speed: AudioMaterial.#random(0.05, 0.1),
      amplitude: 0.2
    }

    const waveY1 = {
      count: AudioMaterial.#random(1, 2),
      speed: -AudioMaterial.#random(0.1, 0.2),
      amplitude: 0.25
    }

    const waveY2 = {
      count: AudioMaterial.#random(1, 4),
      speed: -AudioMaterial.#random(0.05, 0.1),
      amplitude: 0.25
    }

    return {
      waveX0,
      waveX1,
      waveX2,
      waveY0,
      waveY1,
      waveY2
    }
  }
}
